import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { getMetadata, generateUploadUrl, uploadFileToS3, processGoodsReceipt, approvePrices } from '../api/services';
import { formatCurrency, formatDateTime, getPriceListDisplayName } from '../Utils/formatters';

function UpdatePrices({ setFeedback, setUploading, uploading }) {
  // --- ESTADOS INTERNOS DEL COMPONENTE ---
  const [dbMetadata, setDbMetadata] = useState({ databaseExists: false, lastUpdates: null, isLoading: true });
  const [pendingItems, setPendingItems] = useState([]);
  const [approvedItems, setApprovedItems] = useState([]);
  const [selections, setSelections] = useState({});
  const [activeFilter, setActiveFilter] = useState('todos');

  // --- EFECTOS ---
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setDbMetadata(prev => ({ ...prev, isLoading: true }));
        const data = await getMetadata();
        setDbMetadata({ databaseExists: data.databaseExists, lastUpdates: data.lastUpdates, isLoading: false });
      } catch (error) {
        console.error('Error fetching DB metadata:', error);
        setFeedback({ message: 'Error al cargar metadatos.', type: 'error' });
        setFeedback({ message: error.summary || error.message || 'Error al cargar metadatos.', type: 'error' });
        setDbMetadata(prev => ({ ...prev, isLoading: false }));
      }
    };
    fetchInitialData();
  }, [setFeedback]);

  // --- LÓGICA DE MANEJO DE ARCHIVOS Y PROCESOS ---
  const handleGoodsReceiptUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading('goods-receipt');
    setFeedback({ message: `Subiendo ${file.name}...`, type: 'loading' });

    try {
      const { uploadURL, key } = await generateUploadUrl('goods-receipt', file.name);
      const bucket = new URL(uploadURL).hostname.split('.')[0];

      await uploadFileToS3(uploadURL, file);

      setFeedback({ message: 'Archivo subido. Procesando y comparando...', type: 'loading' });

      const comparisonData = await processGoodsReceipt(bucket, key);
      setPendingItems(comparisonData);
      setFeedback({ message: 'Datos listos para comparar y aprobar.', type: 'success' });

    } catch (error) {
      console.error('Error en el proceso de ingreso de mercancía:', error);
      setFeedback({ message: 'Error al subir o procesar el archivo de ingreso.', type: 'error' });
      setFeedback({ message: error.summary || error.message || 'Error al subir o procesar el archivo de ingreso.', type: 'error' });
    } finally {
      setUploading('');
      if (event.target) event.target.value = null;
    }
  };

  const handleSaveAndDownload = async () => {
    if (approvedItems.length === 0) {
      setFeedback({ message: 'No hay items aprobados para descargar.', type: 'error' });
      return;
    }
    setFeedback({ message: 'Guardando aprobaciones y generando archivo...', type: 'loading' });

    try {
      // El backend ahora devuelve el archivo generado
      const response = await approvePrices(approvedItems);

      if (!response.fileContent) {
        throw new Error("El servidor no devolvió el archivo esperado.");
      }

      // Decodificar el archivo base64 y descargarlo
      const byteCharacters = atob(response.fileContent);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.mimeType });

      // Crear enlace de descarga
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setFeedback({ message: 'Proceso completado y archivo descargado.', type: 'success' });
      resetUpdateProcess();
    } catch (error) {
      console.error("Error al guardar o descargar:", error);
      setFeedback({ message: error.summary || error.message || 'Error al generar el archivo.', type: 'error' });
    }
  };

  // --- LÓGICA DE MANEJO DE ESTADO DE UI ---
  const handleSelectionChange = (ref, type, purchaseIndex = null) => {
    setSelections(prev => ({ ...prev, [ref]: { type, index: purchaseIndex } }));
  };

  const handleApprove = () => {
    const itemsToApprove = pendingItems.filter(item => selections[item.Referencia]);
    const remainingItems = pendingItems.filter(item => !selections[item.Referencia]);

    const newlyApproved = itemsToApprove.map(item => {
      const { type, index } = selections[item.Referencia];
      return {
        Referencia: item.Referencia,
        Descripcion: item.Descripcion,
        UnidadMedida: item.UnidadMedida,
        selectedPurchase: type === 'purchase' ? item.NuevasCompras[index] : item.PreciosActuales,
        decision: type
      };
    });

    setApprovedItems(prev => [...prev, ...newlyApproved]);
    setPendingItems(remainingItems);

    const newSelections = { ...selections };
    itemsToApprove.forEach(item => delete newSelections[item.Referencia]);
    setSelections(newSelections);
  };

  const resetUpdateProcess = () => {
    setPendingItems([]);
    setApprovedItems([]);
    setSelections({});
    setActiveFilter('todos');
  };

  const filteredPendingItems = useMemo(() => {
    if (activeFilter === 'todos') return pendingItems;

    return pendingItems.filter(item => {
      // Obtener precios de referencia (Contado/PrecioLista10)
      const precioActualContado = item.PreciosActuales?.PrecioLista10;
      const precioCompraContado = item.NuevasCompras?.[0]?.PrecioLista10;

      // Si no hay precios para comparar, no se puede filtrar
      if (precioActualContado === undefined || precioCompraContado === undefined) {
        return false;
      }

      const conStock = item.ExistenciaResultante > 0;
      const precioSube = precioCompraContado > precioActualContado;
      // CORRECCIÓN 2: Añadir una variable explícita para cuando el precio baja.
      const precioBaja = precioCompraContado < precioActualContado;

      switch (activeFilter) {
        case 'subeconstock': return conStock && precioSube;
        case 'bajaconstock': return conStock && precioBaja;
        case 'subesinstock': return !conStock && precioSube;
        case 'bajasinstock': return !conStock && precioBaja;
        default: return true;
      }
    });
  }, [activeFilter, pendingItems]);

  const handleSelectAll = (type) => {
    const newSelections = {};
    filteredPendingItems.forEach(item => {
      if (type === 'stock' && item.PreciosActuales && Object.keys(item.PreciosActuales).length > 0) {
        newSelections[item.Referencia] = { type: 'stock', index: null };
      } else if (type === 'purchase' && item.NuevasCompras.length > 0) {
        newSelections[item.Referencia] = { type: 'purchase', index: 0 };
      }
    });
    setSelections(prev => ({ ...prev, ...newSelections }));
  };

  const handleDeselectAll = () => {
    const newSelections = { ...selections };
    filteredPendingItems.forEach(item => delete newSelections[item.Referencia]);
    setSelections(newSelections);
  };

  // --- RENDERIZADO DEL COMPONENTE ---
  const renderContent = () => {
    // Define el orden de visualización de las listas de precios.
    const priceOrder = ['PrecioLista10', 'PrecioLista1', 'PrecioLista2', 'PrecioLista4'];

    if (dbMetadata.isLoading) {
      return <div className="spinner" />;
    }

    if (!dbMetadata.databaseExists) {
      return <div className="feedback-message error">No existe una base de datos. Primero debe cargar un "Archivo Maestro".</div>;
    }

    if (pendingItems.length === 0 && approvedItems.length === 0) {
      return (
        <div>
          <div className="status-summary">
            <h3>Estado Actual de los Datos:</h3>
            <ul>
              <li>Última actualización de <strong>Productos</strong>: {formatDateTime(dbMetadata.lastUpdates?.master_file)}</li>
              <li>Última actualización de <strong>Inventario</strong>: {formatDateTime(dbMetadata.lastUpdates?.inventory_file)}</li>
              <li>Última actualización de <strong>Precios</strong>: {formatDateTime(dbMetadata.lastUpdates?.prices_file)}</li>
            </ul>
          </div>
          <div className="file-input-wrapper">
            <label htmlFor="goodsReceiptFile">Subir Archivo de Ingreso de Mercancía</label>
            <input type="file" id="goodsReceiptFile" accept=".xlsx,.csv" onChange={handleGoodsReceiptUpload} disabled={!!uploading} />
          </div>
          {uploading === 'goods-receipt' && <div className="spinner-wrapper"><div className="spinner"></div><p>Subiendo y procesando...</p></div>}
        </div>
      );
    }

    if (pendingItems.length > 0) {
      return (
        <div>
          <div className="actions-bar">
            <div className="filters">
              <label>Filtros: </label>
              <select value={activeFilter} onChange={e => setActiveFilter(e.target.value)}>
                <option value="todos">Mostrar Todos</option>
                <option value="subeconstock">Subirá (con inventario)</option>
                <option value="bajaconstock">Bajará (con inventario)</option>
                <option value="subesinstock">Subirá (sin inventario)</option>
                <option value="bajasinstock">Bajará (sin inventario)</option>

              </select>
            </div>
            <div className="bulk-actions">
              <button onClick={() => handleSelectAll('stock')}>Todos los actuales</button>
              <button onClick={() => handleSelectAll('purchase')}>Todos los de compra</button>
              <button onClick={handleDeselectAll} className="deselect">Deselecionar todo</button>
            </div>
          </div>
          <div className="comparison-table">
            {filteredPendingItems.map(item => (
              <div key={item.Referencia} className="comparison-item">
                <div className="item-header">
                  <h4>{item.Referencia} - {item.Descripcion}</h4>
                  <span>Inventario antes de compra: <strong>{item.ExistenciaResultante}</strong></span>
                </div>
                <div className="options">
                  <div className="option-stock">
                    <label>
                      <input type="radio" name={item.Referencia} checked={selections[item.Referencia]?.type === 'stock'} onChange={() => handleSelectionChange(item.Referencia, 'stock')} />
                      <strong>Precio actual de inventario</strong>
                      <div className="price-comparison-grid">
                        {Object.entries(item.PreciosActuales)
                          .sort(([keyA], [keyB]) => priceOrder.indexOf(keyA) - priceOrder.indexOf(keyB))
                          .map(([lista, precio]) => (
                            <div key={lista} className="price-column current">
                              <div className="price-label">{getPriceListDisplayName(lista)}</div>
                              <div className="price-value">{formatCurrency(precio)}</div>
                            </div>
                          ))}
                      </div>
                    </label>
                  </div>
                  {item.NuevasCompras.map((compra, index) => (
                    <div key={index} className="option-purchase">
                      <label>
                        <input type="radio" name={item.Referencia} checked={selections[item.Referencia]?.type === 'purchase' && selections[item.Referencia]?.index === index} onChange={() => handleSelectionChange(item.Referencia, 'purchase', index)} />
                        <strong>Precios de compra (Costo promedio: {formatCurrency(compra.PrecioCompra)}) (Cantidad comprada: {compra.Cantidad})</strong>
                        <div className="price-comparison-grid">
                          {Object.entries(compra).filter(([key]) => key.startsWith('PrecioLista'))
                            .sort(([keyA], [keyB]) => priceOrder.indexOf(keyA) - priceOrder.indexOf(keyB))
                            .map(([lista, precio]) => (
                              <div key={lista} className="price-column new">
                                <div className="price-label">{getPriceListDisplayName(lista)}</div>
                                <div className="price-value">{formatCurrency(precio)}</div>
                              </div>
                            ))}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="approval-buttons">
            <button className="approve" onClick={handleApprove}>Aceptar Cambios Seleccionados ({Object.keys(selections).length})</button>
            <button className="reject" onClick={resetUpdateProcess}>Rechazar Todo y Empezar de Nuevo</button>
          </div>
        </div>
      );
    }

    if (approvedItems.length > 0) {
      return (
        <div>
          <div className="feedback-message success">Todos los productos han sido procesados.</div>
          <h3>Resumen de Cambios Listos para Descargar</h3>
          <div className="table-wrapper">
            <table className="summary-table">
              <thead>
                <tr>
                  <th>Referencia</th>
                  <th>Descripción</th>
                  <th>Decisión</th>
                </tr>
              </thead>
              <tbody>
                {approvedItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.Referencia}</td>
                    <td>{item.Descripcion}</td>
                    <td>{item.decision === 'stock' ? 'Se mantenerán los precios actuales' : 'Se actualizarán los precios'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="approval-buttons">
            <button onClick={handleSaveAndDownload} className="approve">Guardar y Descargar Archivo</button>
            <button onClick={resetUpdateProcess} className="reject">Iniciar Nuevo Proceso</button>
          </div>
        </div>
      );
    }
  };

  return (
    <section>
      <h2>Ingreso de Mercancía y Actualización de Precios</h2>
      {renderContent()}
    </section>
  );
}

export default UpdatePrices;